import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const PLANS = {
  FREE: {
    id: 'FREE',
    name: 'Free',
    price: 0,
    priceYearly: 0,
    description: 'Perfect for testing',
    features: [
      '1 raffle per month',
      'Up to 100 entries',
      'Basic support',
      'Public raffle pages'
    ]
  },
  STARTER: {
    id: 'STARTER',
    name: 'Starter',
    price: 29,
    priceYearly: 290,
    description: 'Great for growing businesses',
    features: [
      '10 raffles per month',
      'Up to 1,000 entries per raffle',
      'Custom branding',
      'Email notifications',
      'Priority support'
    ],
    popular: true
  },
  PROFESSIONAL: {
    id: 'PROFESSIONAL',
    name: 'Professional',
    price: 79,
    priceYearly: 790,
    description: 'For serious marketers',
    features: [
      '50 raffles per month',
      'Up to 10,000 entries per raffle',
      'Advanced analytics',
      'API access',
      'White-label options',
      'Dedicated support'
    ]
  },
  AGENCY: {
    id: 'AGENCY',
    name: 'Agency',
    price: 199,
    priceYearly: 1990,
    description: 'Unlimited power for agencies',
    features: [
      'Unlimited raffles',
      'Unlimited entries',
      'Full white-label',
      'Multi-location support',
      'Premium API access',
      '24/7 priority support',
      'Custom integrations'
    ]
  }
};

function Pricing() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (searchParams.get('canceled') === 'true') {
      setError('Payment was canceled. Please try again.');
    }
  }, [searchParams]);

  const handleSelectPlan = async (planId, paymentMethod) => {
    if (planId === 'FREE') {
      navigate('/dashboard');
      return;
    }

    if (!token) {
      navigate('/login?redirect=/pricing');
      return;
    }

    setLoading(`${planId}-${paymentMethod}`);
    setError('');

    try {
      if (paymentMethod === 'stripe') {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/billing/stripe/checkout`,
          { planId, billingCycle }
        );
        window.location.href = response.data.data.url;
      } else if (paymentMethod === 'paypal') {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/billing/paypal/subscription`,
          { planId, billingCycle }
        );
        window.location.href = response.data.data.approvalUrl;
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.error || 'Failed to initiate payment');
      setLoading(null);
    }
  };

  return (
    <div className="container" style={{ maxWidth: '1400px', padding: '40px 20px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '60px' }}>
        <h1 style={{ fontSize: '42px', marginBottom: '16px' }}>Choose Your Plan</h1>
        <p style={{ fontSize: '18px', color: '#666', marginBottom: '32px' }}>
          Start free, upgrade when you need more power
        </p>

        {/* Billing Toggle */}
        <div style={{ display: 'inline-flex', background: '#f0f0f0', borderRadius: '50px', padding: '4px' }}>
          <button
            onClick={() => setBillingCycle('monthly')}
            style={{
              padding: '10px 30px',
              borderRadius: '50px',
              border: 'none',
              background: billingCycle === 'monthly' ? '#007bff' : 'transparent',
              color: billingCycle === 'monthly' ? 'white' : '#666',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            style={{
              padding: '10px 30px',
              borderRadius: '50px',
              border: 'none',
              background: billingCycle === 'yearly' ? '#007bff' : 'transparent',
              color: billingCycle === 'yearly' ? 'white' : '#666',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Yearly <span style={{ color: '#28a745', fontWeight: 'bold' }}>Save 17%</span>
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          background: '#f8d7da',
          color: '#721c24',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '30px',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      {/* Plan Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '30px',
        marginBottom: '60px'
      }}>
        {Object.values(PLANS).map(plan => {
          const price = billingCycle === 'yearly' ? plan.priceYearly : plan.price;
          const savings = billingCycle === 'yearly' && plan.price > 0
            ? (plan.price * 12 - plan.priceYearly)
            : 0;

          return (
            <div
              key={plan.id}
              className="card"
              style={{
                position: 'relative',
                border: plan.popular ? '3px solid #007bff' : '1px solid #ddd',
                transform: plan.popular ? 'scale(1.05)' : 'none',
                transition: 'transform 0.3s'
              }}
            >
              {plan.popular && (
                <div style={{
                  position: 'absolute',
                  top: '-15px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#007bff',
                  color: 'white',
                  padding: '5px 20px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>
                  MOST POPULAR
                </div>
              )}

              <h3 style={{ fontSize: '24px', marginBottom: '8px' }}>{plan.name}</h3>
              <p style={{ color: '#666', marginBottom: '20px' }}>{plan.description}</p>

              <div style={{ marginBottom: '24px' }}>
                <span style={{ fontSize: '48px', fontWeight: 'bold' }}>
                  ${price}
                </span>
                <span style={{ color: '#666', fontSize: '16px' }}>
                  /{billingCycle === 'yearly' ? 'year' : 'month'}
                </span>
                {savings > 0 && (
                  <div style={{ color: '#28a745', fontSize: '14px', marginTop: '4px' }}>
                    Save ${savings}/year
                  </div>
                )}
              </div>

              <ul style={{ listStyle: 'none', padding: 0, marginBottom: '24px' }}>
                {plan.features.map((feature, index) => (
                  <li key={index} style={{
                    padding: '8px 0',
                    borderBottom: index < plan.features.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}>
                    <span style={{ color: '#28a745', marginRight: '8px' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              {plan.id === 'FREE' ? (
                <button
                  onClick={() => handleSelectPlan('FREE')}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Get Started Free
                </button>
              ) : (
                <div>
                  <button
                    onClick={() => handleSelectPlan(plan.id, 'stripe')}
                    disabled={loading === `${plan.id}-stripe`}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: plan.popular ? '#007bff' : '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      marginBottom: '10px'
                    }}
                  >
                    {loading === `${plan.id}-stripe` ? 'Loading...' : '💳 Pay with Card (Stripe)'}
                  </button>

                  <button
                    onClick={() => handleSelectPlan(plan.id, 'paypal')}
                    disabled={loading === `${plan.id}-paypal`}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: '#0070ba',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    {loading === `${plan.id}-paypal` ? 'Loading...' : 'Pay with PayPal'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* FAQ Section */}
      <div style={{ textAlign: 'center', marginTop: '80px' }}>
        <h2 style={{ marginBottom: '16px' }}>Frequently Asked Questions</h2>

        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'left' }}>
          <div className="card" style={{ marginBottom: '16px' }}>
            <h4>Can I change plans later?</h4>
            <p style={{ color: '#666' }}>
              Yes! You can upgrade or downgrade your plan at any time from your dashboard.
            </p>
          </div>

          <div className="card" style={{ marginBottom: '16px' }}>
            <h4>What payment methods do you accept?</h4>
            <p style={{ color: '#666' }}>
              We accept all major credit cards via Stripe and PayPal subscriptions.
            </p>
          </div>

          <div className="card" style={{ marginBottom: '16px' }}>
            <h4>Can I cancel anytime?</h4>
            <p style={{ color: '#666' }}>
              Absolutely! Cancel anytime from your dashboard. No questions asked.
            </p>
          </div>

          <div className="card">
            <h4>Do you offer refunds?</h4>
            <p style={{ color: '#666' }}>
              Yes, we offer a 14-day money-back guarantee on all paid plans.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Pricing;
